import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { type JSX, useMemo } from 'react'
import {
  Combobox,
  type ComboboxOption,
  type ComboboxProps,
} from '#src/components/core/combobox/Combobox.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'

interface TaskStateComboboxProps extends Omit<
  ComboboxProps<StandardTask['state']>,
  'options'
> {
  selected: StandardTask['state']
}

export function TaskStateCombobox({
  selected,
  ...delegated
}: TaskStateComboboxProps): JSX.Element {
  const options: ComboboxOption<StandardTask['state']>[] = useMemo(
    () => [
      {
        value: 'todo',
        label: (
          <TaskStateOption state="todo" isSelected={selected === 'todo'} />
        ),
        keywords: ['not started'],
      },
      {
        value: 'in-progress',
        label: (
          <TaskStateOption
            state="in-progress"
            isSelected={selected === 'in-progress'}
          />
        ),
        keywords: ['in progress'],
      },
      {
        value: 'done',
        label: (
          <TaskStateOption state="done" isSelected={selected === 'done'} />
        ),
        keywords: ['done'],
      },
      {
        value: 'backlog',
        label: (
          <TaskStateOption
            state="backlog"
            isSelected={selected === 'backlog'}
          />
        ),
        keywords: ['backlog'],
      },
      {
        value: 'canceled',
        label: (
          <TaskStateOption
            state="canceled"
            isSelected={selected === 'canceled'}
          />
        ),
        keywords: ['canceled'],
      },
      {
        value: 'duplicate',
        label: (
          <TaskStateOption
            state="duplicate"
            isSelected={selected === 'duplicate'}
          />
        ),
        keywords: ['duplicate'],
      },
    ],
    [selected]
  )
  return <Combobox options={options} {...delegated} />
}

function TaskStateOption({
  state,
  isSelected,
}: {
  state: StandardTask['state']
  isSelected: boolean
}): JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="ml-0.5 flex items-center gap-2">
        <IconTaskState state={state} />
        <TaskStateText state={state} />
      </div>
      <IconCheck
        className={cn(
          'mr-0.5 size-4 shrink-0 opacity-0',
          isSelected && 'opacity-100'
        )}
      />
    </div>
  )
}
